import { useCallback } from "react";

const CONFETTI_KEYFRAMES_ID = "confetti-pom-keyframes";
const CONFETTI_PIECES = 120;
const CONFETTI_LIFETIME = 3000;
const CONFETTI_COLORS = [
  "#ff595e",
  "#ffca3a",
  "#8ac926",
  "#1982c4",
  "#6a4c93",
  "#ff924c",
  "#ff70a6",
];

/**
 * Hook that throws a burst of colorful confetti "poms" across the screen.
 *
 * Builds a fixed, full-viewport, non-interactive overlay of falling/spinning
 * pieces, injects the falling keyframes once, then removes the overlay after
 * the pieces have finished falling. Fully self-contained — no libraries and no
 * lasting DOM.
 */
export const useConfetti = () => {
  const throwConfetti = useCallback(() => {
    if (typeof document === "undefined") {
      return;
    }

    if (!document.getElementById(CONFETTI_KEYFRAMES_ID)) {
      const style = document.createElement("style");
      style.id = CONFETTI_KEYFRAMES_ID;
      style.textContent =
        "@keyframes confetti-pom-fall{" +
        "0%{transform:translateY(-10vh) rotate(0deg);opacity:1;}" +
        "100%{transform:translateY(110vh) rotate(720deg);opacity:1;}}";
      document.head.appendChild(style);
    }

    const container = document.createElement("div");
    container.setAttribute("aria-hidden", "true");
    container.style.cssText =
      "position:fixed;inset:0;pointer-events:none;overflow:hidden;z-index:99999;";

    for (let i = 0; i < CONFETTI_PIECES; i++) {
      const pom = document.createElement("div");
      const size = 8 + Math.random() * 8;
      const color = CONFETTI_COLORS[i % CONFETTI_COLORS.length];
      const delay = Math.random() * 500;
      const fallDuration = 2000 + Math.random() * 1000;
      pom.style.cssText =
        `position:absolute;top:0;left:${Math.random() * 100}vw;` +
        `width:${size}px;height:${size}px;background:${color};` +
        `border-radius:${Math.random() < 0.5 ? "50%" : "2px"};` +
        `animation:confetti-pom-fall ${fallDuration}ms linear ${delay}ms forwards;`;
      container.appendChild(pom);
    }

    document.body.appendChild(container);
    window.setTimeout(() => {
      container.remove();
    }, CONFETTI_LIFETIME);
  }, []);

  return { throwConfetti };
};
